import React, { useState, useRef } from "react";
import {
    Box,
    Paper,
    Stack,
    Typography,
    Chip,
    useTheme,
} from "@mui/material";

import { UserAvatarStack } from "../../../../components/ui/userChip";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragOverlay,
} from "@dnd-kit/core";
import {
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const formatDateShort = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    const year = date.getUTCFullYear();
    const month = date.getUTCMonth() + 1;
    const day = date.getUTCDate();
    return `${day}/${month}/${String(year).slice(-2)}`;
};

// Sortable task item component
const SortableTask = ({
    task,
    onTaskClick,
    getPriorityColor,
    getPriorityBackground,
    getTaskDescription,
    formatDateShort,
    getPriorityIcon,
    getEmployeeName,
    theme,
}) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging: isSortableDragging,
    } = useSortable({
        id: task.id,
        data: {
            type: 'task',
            status: task.status,
        }
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition: isSortableDragging ? 'none' : transition,
        opacity: isSortableDragging ? 0.4 : 1,
        cursor: isSortableDragging ? 'grabbing' : 'grab',
    };

    return (
        <Paper
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            onClick={() => onTaskClick(task)}
            elevation={isSortableDragging ? 4 : 1}
            sx={{
                p: 2,
                cursor: "grab",
                borderRadius: "10px",
                borderLeft: `4px solid ${getPriorityColor(task.priority)}`,
                background: getPriorityBackground(
                    task.priority,
                    theme.palette.mode === "dark"
                ),
                transition: theme.transitions.create(['transform', 'box-shadow', 'opacity'], {
                    duration: 200,
                }),
                "&:hover": {
                    transform: "translateY(-3px)",
                    boxShadow: theme.shadows[6],
                },
                touchAction: "none",
                userSelect: "none",
                position: "relative",
                '&:active': {
                    cursor: "grabbing",
                },
            }}
        >
            {/* Header: Title */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    mb: 0.5,
                }}
            >
                <Typography
                    sx={{
                        fontWeight: 700,
                        fontSize: "0.95rem",
                        color: theme.palette.text.primary,
                        flex: 1,
                        pr: 0.5,
                    }}
                >
                    {task.title}
                </Typography>
            </Box>

            <Typography
                sx={{
                    mb: 1,
                    fontSize: "0.8rem",
                    color: theme.palette.text.secondary,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    lineHeight: 1.4,
                }}
            >
                {getTaskDescription(task) || "No description provided"}
            </Typography>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                }}
            >
                <Typography
                    variant="caption"
                    sx={{
                        display: "block",
                        mb: 1,
                        color: theme.palette.text.secondary,
                        fontSize: "0.7rem",
                    }}
                >
                    {formatDateShort(task.start_date)}
                    {" - "}
                    {formatDateShort(task.due_date)}
                </Typography>

                <Chip
                    size="small"
                    icon={getPriorityIcon(task.priority)}
                    label={task.priority || "Priority"}
                    sx={{
                        height: 22,
                        fontSize: "0.65rem",
                        fontWeight: 600,
                    }}
                />
            </Box>
            {(() => {
                let users = [];
                if (task.assignees?.length > 0) {
                    users = task.assignees.map(a => ({
                        id: a.employee_id,
                        name: getEmployeeName(a.employee_id),
                        status: a.status
                    }));
                } else if (task.assigned_to) {
                    users = [{
                        id: task.assigned_to,
                        name: getEmployeeName(task.assigned_to),
                        status: task.status
                    }];
                }
                return <UserAvatarStack users={users} />;
            })()}
        </Paper>
    );
};

// Column component
const TaskColumn = ({
    status,
    tasks,
    onTaskClick,
    getPriorityColor,
    getPriorityBackground,
    getTaskDescription,
    formatDateShort,
    getPriorityIcon,
    getEmployeeName,
    getStatusLabel,
    theme,
    isOver,
}) => {
    const taskIds = tasks.map(task => task.id);

    const { setNodeRef } = useSortable({
        id: `column-${status}`,
        data: {
            type: 'column',
            status: status,
        }
    });

    return (
        <Box
            ref={setNodeRef}
            id={`column-${status}`}
            sx={{
                flex: 1,
                minWidth: 280,
                maxWidth: 320,
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: isOver
                    ? theme.palette.mode === "dark"
                        ? 'rgba(245, 158, 11, 0.08)'
                        : 'rgba(245, 158, 11, 0.05)'
                    : 'transparent',
                borderRadius: '12px',
                transition: theme.transitions.create('background-color', {
                    duration: 200,
                }),
                border: `1px solid ${theme.palette.mode === "dark"
                    ? 'rgba(255,255,255,0.06)'
                    : 'rgba(0,0,0,0.06)'}`,
            }}
        >
            {/* Column Header */}
            <Box
                sx={{
                    p: 2,
                    pb: 1.5,
                    borderBottom: `1px solid ${theme.palette.mode === "dark"
                        ? 'rgba(255,255,255,0.06)'
                        : 'rgba(0,0,0,0.06)'}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                <Typography
                    sx={{
                        fontWeight: 700,
                        fontSize: "0.85rem",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        color: "text.secondary",
                    }}
                >
                    {getStatusLabel(status)}
                </Typography>
                <Chip
                    label={tasks.length}
                    size="small"
                    sx={{
                        height: 22,
                        fontSize: "0.7rem",
                        fontWeight: 600,
                        backgroundColor: theme.palette.mode === "dark"
                            ? 'rgba(255,255,255,0.08)'
                            : 'rgba(0,0,0,0.06)',
                    }}
                />
            </Box>

            {/* Tasks Container */}
            <Box
                sx={{
                    flex: 1,
                    p: 1.5,
                    minHeight: 200,
                    '&::-webkit-scrollbar': {
                        width: 4,
                    },
                    '&::-webkit-scrollbar-track': {
                        background: 'transparent',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        background: theme.palette.mode === "dark"
                            ? 'rgba(255,255,255,0.1)'
                            : 'rgba(0,0,0,0.1)',
                        borderRadius: '10px',
                    },
                }}
            >
                <SortableContext
                    items={taskIds}
                    strategy={verticalListSortingStrategy}
                >
                    <Stack spacing={1.5}>
                        {tasks.map((task) => (
                            <SortableTask
                                key={task.id}
                                task={task}
                                onTaskClick={onTaskClick}
                                getPriorityColor={getPriorityColor}
                                getPriorityBackground={getPriorityBackground}
                                getTaskDescription={getTaskDescription}
                                formatDateShort={formatDateShort}
                                getPriorityIcon={getPriorityIcon}
                                getEmployeeName={getEmployeeName}
                                theme={theme}
                            />
                        ))}
                    </Stack>
                </SortableContext>
            </Box>
        </Box>
    );
};

// Drag overlay component
const DragOverlayTask = ({ task, getPriorityColor, getPriorityBackground, getTaskDescription, formatDateShort, getPriorityIcon, getEmployeeName, theme }) => {
    return (
        <Paper
            elevation={8}
            sx={{
                p: 2,
                borderRadius: "8px",
                borderLeft: `4px solid ${getPriorityColor(task.priority)}`,
                background: getPriorityBackground(
                    task.priority,
                    theme.palette.mode === "dark"
                ),
                width: 280,
                cursor: "grabbing",
                transform: "rotate(2deg) scale(1.02)",
                boxShadow: theme.shadows[8],
            }}
        >
            <Typography
                sx={{
                    fontWeight: 700,
                    mb: 0.75,
                    fontSize: "0.95rem",
                    color: theme.palette.text.primary,
                }}
            >
                {task.title}
            </Typography>
            <Typography
                sx={{
                    mb: 1,
                    fontSize: "0.8rem",
                    color: theme.palette.text.secondary,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                }}
            >
                {getTaskDescription(task) || "No description provided"}
            </Typography>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ gap: 0.5 }}>
                <Chip
                    size="small"
                    icon={getPriorityIcon(task.priority)}
                    label={task.priority || "Priority"}
                    sx={{ height: 32, fontSize: "0.65rem", fontWeight: 600 }}
                />
                {(() => {
                    let users = [];
                    if (task.assignees?.length > 0) {
                        users = task.assignees.map(a => ({
                            id: a.employee_id,
                            name: getEmployeeName(a.employee_id),
                            status: a.status
                        }));
                    } else if (task.assigned_to) {
                        users = [{
                            id: task.assigned_to,
                            name: getEmployeeName(task.assigned_to),
                            status: task.status
                        }];
                    }
                    return <UserAvatarStack users={users} />;
                })()}
            </Stack>
        </Paper>
    );
};

const TaskBoard = ({
    tasks,
    projects,
    employees,
    onEditTask,
    onDeleteTask,
    onStatusChange,
    onTaskClick,
    getProjectName,
    getEmployeeName,
    getTaskDescription,
    getStatusLabel,
    getStatusColor,
    getPriorityColor,
    getPriorityIcon,
    hasPermission,
}) => {
    const theme = useTheme();
    const [activeId, setActiveId] = useState(null);
    const boardRef = useRef(null);

    const getPriorityBackground = (priority, dark) => {
        switch (priority?.toLowerCase()) {
            case "urgent":
                return dark
                    ? "rgba(220,38,38,0.15)"
                    : "rgba(254,226,226,0.8)";
            case "high":
                return dark
                    ? "rgba(249,115,22,0.15)"
                    : "rgba(255,237,213,0.8)";
            case "medium":
                return dark
                    ? "rgba(234,179,8,0.15)"
                    : "rgba(254,249,195,0.8)";
            case "low":
                return dark
                    ? "rgba(34,197,94,0.15)"
                    : "rgba(220,252,231,0.8)";
            default:
                return dark
                    ? "rgba(30,41,59,0.5)"
                    : "rgba(255,255,255,0.8)";
        }
    };

    const statusColumns = [
        "todo",
        "in_progress",
        "review",
        "paused",
        "blocked",
        "done",
    ];

    // Group tasks by status
    const tasksByStatus = {};
    statusColumns.forEach(status => {
        tasksByStatus[status] = tasks.filter(task => task.status === status);
    });

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleDragStart = (event) => {
        setActiveId(event.active.id);
    };

    const handleDragEnd = (event) => {
        setActiveId(null);
        const { active, over } = event;

        if (!over) return;

        const activeId = active.id;
        const overId = over.id;

        // Find the dragged task
        const draggedTask = tasks.find(t => t.id === activeId);
        if (!draggedTask) return;

        // Determine the target status
        let targetStatus = null;

        // Check if dropped on a column
        if (overId && overId.toString().startsWith('column-')) {
            targetStatus = overId.toString().replace('column-', '');
        }
        // Check if dropped on another task
        else {
            const overTask = tasks.find(t => t.id === overId);
            if (overTask) {
                targetStatus = overTask.status;
            }
        }

        if (!targetStatus) return;

        // If status changed, call handleStatusChange
        if (draggedTask.status !== targetStatus) {
            onStatusChange(targetStatus, draggedTask);
        }
    };

    const handleDragCancel = () => {
        setActiveId(null);
    };

    // Find the active task for the overlay
    const activeTask = activeId ? tasks.find(t => t.id === activeId) : null;

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
        >
            <Box
                id="task-board-container"
                ref={boardRef}
                sx={{
                    display: "flex",
                    gap: 1,
                    overflowX: "auto",
                    pb: 2,
                    alignItems: "flex-start",
                    height: "100%",
                    minHeight: "400px",
                    px: 0.5,
                    scrollBehavior: "smooth",
                    '&::-webkit-scrollbar': {
                        height: 6,
                    },
                    '&::-webkit-scrollbar-track': {
                        background: 'transparent',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        background: theme.palette.mode === "dark"
                            ? 'rgba(255,255,255,0.1)'
                            : 'rgba(0,0,0,0.1)',
                        borderRadius: '10px',
                    },
                }}
            >
                {statusColumns.map((status) => {
                    const columnTasks = tasksByStatus[status] || [];

                    return (
                        <TaskColumn
                            key={status}
                            status={status}
                            tasks={columnTasks}
                            onTaskClick={onTaskClick}
                            getPriorityColor={getPriorityColor}
                            getPriorityBackground={getPriorityBackground}
                            getTaskDescription={getTaskDescription}
                            formatDateShort={formatDateShort}
                            getPriorityIcon={getPriorityIcon}
                            getEmployeeName={getEmployeeName}
                            getStatusLabel={getStatusLabel}
                            theme={theme}
                            isOver={false}
                        />
                    );
                })}
            </Box>

            {/* Drag Overlay */}
            <DragOverlay
                dropAnimation={{
                    duration: 200,
                    easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)',
                }}
            >
                {activeTask ? (
                    <DragOverlayTask
                        task={activeTask}
                        getPriorityColor={getPriorityColor}
                        getPriorityBackground={getPriorityBackground}
                        getTaskDescription={getTaskDescription}
                        formatDateShort={formatDateShort}
                        getPriorityIcon={getPriorityIcon}
                        getEmployeeName={getEmployeeName}
                        theme={theme}
                    />
                ) : null}
            </DragOverlay>
        </DndContext>
    );
};

export default TaskBoard;
